// src/components/tutor/mobile/MobileTutorNotifications.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { LuX } from "react-icons/lu";
import { auth, db } from "../../../firebaseConfig";
import "./MobileTutorNotifications.css";

// ─── Type config ──────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  announcement:            { label: "Announcement",        dot: "#6B7280" },
  session_created:         { label: "New Session",         dot: "#16A34A" },
  session_cancelled:       { label: "Session Cancelled",   dot: "#DC2626" },
  session_restored:        { label: "Session Restored",    dot: "#D97706" },
  session_time_changed:    { label: "Time Changed",        dot: "#2563EB" },
  session_date_changed:    { label: "Date Changed",        dot: "#7C3AED" },
  tutor_reassigned_in:     { label: "You Were Assigned",   dot: "#16A34A" },
  tutor_reassigned_out:    { label: "Session Reassigned",  dot: "#D97706" },
  session_no_show:         { label: "No Show",             dot: "#DC2626" },
  session_subject_changed: { label: "Subject Changed",     dot: "#0891B2" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDate(ts) {
  if (!ts) return null;
  return ts.toDate ? ts.toDate() : new Date(ts);
}

function dayLabel(ts) {
  const date = toDate(ts);
  if (!date) return "";
  const now  = new Date();
  const diff = Math.floor((now - date) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7)  return date.toLocaleDateString("en-US", { weekday: "long" });
  return date.toLocaleDateString("en-US", {
    month: "short", day: "numeric",
    year: Math.floor((now - date) / 86400000) > 365 ? "numeric" : undefined,
  });
}

function timeStr(ts) {
  const date = toDate(ts);
  if (!date) return "";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// ─── Swipeable card ───────────────────────────────────────────────────────────

const REVEAL_PX  = 80;   // distance to show delete button
const COMMIT_PX  = 200;  // distance for instant-delete (long intentional swipe)
const EDGE_SWIPE_START_PX = 32;
const EDGE_SWIPE_COMMIT_PX = 100;

function SwipeableCard({ item, onDelete, onNavigate, isFirst, isLast, isOnly }) {
  const touchStartX  = useRef(null);
  const touchStartY  = useRef(null);
  const isTouching   = useRef(false);
  const [offset,   setOffset]   = useState(0);
  const [revealed, setRevealed] = useState(false);

  const reset = () => { setOffset(0); setRevealed(false); };

  const handleTouchStart = (e) => {
    // If another row is revealed, tapping this row just collapses it
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isTouching.current  = true;
  };

  const handleTouchMove = (e) => {
    if (!isTouching.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);

    // Vertical scroll takes priority — abort horizontal tracking
    if (dy > 12 && Math.abs(dx) < dy) {
      isTouching.current = false;
      reset();
      return;
    }

    // Ignore right swipe
    if (dx > 0) {
      if (!revealed) return;
      setOffset(Math.min(0, -REVEAL_PX + dx));
      return;
    }

    // Left swipe — clamp so the row doesn't over-shoot the delete button
    const raw = revealed ? -REVEAL_PX + dx : dx;
    setOffset(Math.max(raw, -COMMIT_PX));
  };

  const handleTouchEnd = () => {
    isTouching.current = false;
    if (offset < -COMMIT_PX + 20) {
      // Long intentional swipe — delete immediately
      onDelete(item.id);
    } else if (offset < -REVEAL_PX) {
      // Snap to revealed state and show Delete button
      setRevealed(true);
      setOffset(-REVEAL_PX);
    } else {
      // Snap back
      reset();
    }
  };

  const handleCardClick = () => {
    if (revealed) { reset(); return; }
    if (item.targetDate) onNavigate?.(item.targetDate);
  };

  const cfg = TYPE_CONFIG[item.type] || { label: "Notification", dot: "#9CA3AF" };

  // Build border-radius classes based on position within group
  let radiusClass = "";
  if (isOnly)        radiusClass = "mtn-card--only";
  else if (isFirst)  radiusClass = "mtn-card--first";
  else if (isLast)   radiusClass = "mtn-card--last";

  return (
    <div className={`mtn-swipe-row ${radiusClass}`}>
      {/* Delete background — revealed behind the sliding card */}
      <div
        className="mtn-delete-bg"
        aria-hidden="true"
      >
        <button
          className="mtn-delete-btn"
          onClick={() => onDelete(item.id)}
          aria-label="Delete notification"
        >
          Delete
        </button>
      </div>

      {/* Sliding card */}
      <div
        className={`mtn-card ${item.targetDate ? "mtn-card--tappable" : ""}`}
        style={{
          transform:  `translateX(${offset}px)`,
          transition: isTouching.current ? "none" : "transform 0.22s ease",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleCardClick}
      >
        <div className="mtn-card-left">
          <span className="mtn-dot" style={{ background: cfg.dot }} aria-hidden="true" />
        </div>
        <div className="mtn-card-body">
          <div className="mtn-card-meta">
            <span className="mtn-type-tag">{cfg.label}</span>
            <span className="mtn-card-time">{timeStr(item.createdAt)}</span>
          </div>
          <p className="mtn-card-title">{item.title}</p>
          {item.body && <p className="mtn-card-sub">{item.body}</p>}
        </div>
        {item.targetDate && (
          <div className="mtn-card-chevron" aria-hidden="true">›</div>
        )}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function MobileTutorNotifications({ onClose, onNavigate }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [edgeSwipeDx, setEdgeSwipeDx] = useState(0);
  const [edgeSwipeClosing, setEdgeSwipeClosing] = useState(false);
  const edgeSwipeTimerRef = useRef(null);
  const edgeStartXRef = useRef(null);
  const edgeStartYRef = useRef(null);
  const edgeSwipeActiveRef = useRef(false);
  const edgeSwipeDxRef = useRef(0);
  const edgeSwipeDraggingRef = useRef(false);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setLoading(false); return; }

    const q = query(
      collection(db, "tutor_notifications"),
      where("uid", "==", uid),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    getDocs(q)
      .then((snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => {
        console.error("Error loading notification history:", err);
        setError("Could not load notifications. Please try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = useCallback(async (id) => {
    // Optimistic remove from UI
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await deleteDoc(doc(db, "tutor_notifications", id));
    } catch (err) {
      console.error("Failed to delete notification:", err);
      // No rollback — deletion failure is non-critical
    }
  }, []);

  useEffect(() => {
    return () => {
      if (edgeSwipeTimerRef.current) {
        window.clearTimeout(edgeSwipeTimerRef.current);
      }
    };
  }, []);

  const resetEdgeSwipe = useCallback(() => {
    edgeSwipeActiveRef.current = false;
    edgeSwipeDraggingRef.current = false;
    edgeSwipeDxRef.current = 0;
    setEdgeSwipeDx(0);
  }, []);

  const handleEdgeSwipeStart = useCallback((e) => {
    if (!onClose || e.touches.length !== 1) return;
    const x = e.touches[0].clientX;
    if (x > EDGE_SWIPE_START_PX) return;
    edgeStartXRef.current = x;
    edgeStartYRef.current = e.touches[0].clientY;
    edgeSwipeActiveRef.current = true;
    edgeSwipeDraggingRef.current = true;
    edgeSwipeDxRef.current = 0;
    setEdgeSwipeClosing(false);
  }, [onClose]);

  const handleEdgeSwipeMove = useCallback((e) => {
    if (!edgeSwipeActiveRef.current) return;
    const dx = e.touches[0].clientX - edgeStartXRef.current;
    const dy = Math.abs(e.touches[0].clientY - edgeStartYRef.current);

    if (dy > 20 && dy > Math.abs(dx)) {
      resetEdgeSwipe();
      return;
    }
    if (dx <= 0) return;

    edgeSwipeDxRef.current = dx;
    setEdgeSwipeDx(dx);
  }, [resetEdgeSwipe]);

  const handleEdgeSwipeEnd = useCallback(() => {
    if (!edgeSwipeActiveRef.current) return;
    edgeSwipeActiveRef.current = false;
    edgeSwipeDraggingRef.current = false;
    const dx = edgeSwipeDxRef.current;

    if (dx > EDGE_SWIPE_COMMIT_PX) {
      setEdgeSwipeClosing(true);
      setEdgeSwipeDx(0);
      edgeSwipeDxRef.current = 0;
      edgeSwipeTimerRef.current = window.setTimeout(() => {
        onClose?.();
      }, 220);
      return;
    }
    setEdgeSwipeDx(0);
    edgeSwipeDxRef.current = 0;
  }, [onClose]);

  // Group rows by calendar day
  const grouped = [];
  let lastDay = null;
  for (const n of items) {
    const day = dayLabel(n.createdAt);
    if (day !== lastDay) {
      grouped.push({ kind: "header", day });
      lastDay = day;
    }
    grouped.push({ kind: "item", ...n });
  }

  // Build contiguous groups so we can assign first/last/only border-radius
  // A "group" is a run of items between two headers (or start/end)
  const cardGroups = [];
  let currentGroup = [];
  for (const row of grouped) {
    if (row.kind === "header") {
      if (currentGroup.length) { cardGroups.push([...currentGroup]); currentGroup = []; }
      cardGroups.push([{ kind: "header", day: row.day }]);
    } else {
      currentGroup.push(row);
    }
  }
  if (currentGroup.length) cardGroups.push([...currentGroup]);

  return (
    <div
      className={`mtn-container${edgeSwipeClosing ? " mtn-container--closing" : ""}${edgeSwipeDraggingRef.current ? " mtn-container--dragging" : ""}`}
      style={edgeSwipeDx > 0 ? { transform: `translateX(${edgeSwipeDx}px)` } : undefined}
      onTouchStartCapture={handleEdgeSwipeStart}
      onTouchMoveCapture={handleEdgeSwipeMove}
      onTouchEndCapture={handleEdgeSwipeEnd}
      onTouchCancelCapture={handleEdgeSwipeEnd}
    >
      {/* ── Header (matches Announcements style) ── */}
      <div className="mtn-header">
        <h1 className="mtn-title">Notifications</h1>
        {onClose && (
          <button
            className="mtn-close-btn"
            onClick={onClose}
            aria-label="Close notifications"
          >
            <LuX size={20} />
          </button>
        )}
      </div>

      {/* ── Scrollable feed ── */}
      <div className="mtn-feed">
        {loading && (
          <div className="mtn-state">
            <p className="mtn-state-text">Loading…</p>
          </div>
        )}

        {error && (
          <div className="mtn-state">
            <p className="mtn-state-text mtn-state-text--error">{error}</p>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="mtn-state">
            <p className="mtn-state-heading">No notifications yet</p>
            <p className="mtn-state-text">
              Session and announcement alerts will appear here once they arrive.
            </p>
          </div>
        )}

        {!loading && !error && cardGroups.map((group, gi) => {
          if (group.length === 1 && group[0].kind === "header") {
            return (
              <p key={`h-${gi}`} className="mtn-day-label">
                {group[0].day}
              </p>
            );
          }
          // Item group — render with rounded corners only on first/last
          return (
            <div key={`g-${gi}`} className="mtn-group">
              {group.map((item, ii) => (
                <SwipeableCard
                  key={item.id}
                  item={item}
                  onDelete={handleDelete}
                  onNavigate={onNavigate}
                  isFirst={ii === 0}
                  isLast={ii === group.length - 1}
                  isOnly={group.length === 1}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
